import { SimulationContext } from "../simulation/simulation_context.js";

export class BlockMassTuner
{
  public static readonly MASS_MIN = 0;
  public static readonly MASS_MAX = 100;

  private static readonly HARD_MIN = 0.01;
  private static readonly HARD_MAX = 100000;

  private static active_key: string | null = null;
  private static on_base_recorded: ( () => void ) | null = null;
  private static readonly multipliers = new Map<string, number>();
  private static readonly base_masses = new Map<string, number>();

  public static setBaseListener( listener: ( () => void ) | null ): void
  {
    this.on_base_recorded = listener;
  }

  public static setActiveKey( key: string | null ): void
  {
    this.active_key = key;
  }

  public static getActiveKey(): string | null
  {
    return this.active_key;
  }

  public static applyMass( mass: number ): number
  {
    const key = this.active_key;
    if ( key === null || SimulationContext.enabled )
      return mass;

    if ( Number.isFinite( mass ) && mass > 0 && this.base_masses.get( key ) !== mass )
    {
      this.base_masses.set( key, mass );
      this.on_base_recorded?.();
    }

    return mass * ( this.multipliers.get( key ) ?? 1 );
  }

  public static getBaseMass( key: string | null = this.active_key ): number | null
  {
    if ( key === null )
      return null;
    return this.base_masses.get( key ) ?? null;
  }

  public static getMultiplier( key: string | null = this.active_key ): number
  {
    if ( key === null )
      return 1;
    return this.multipliers.get( key ) ?? 1;
  }

  public static getMass( key: string | null = this.active_key ): number | null
  {
    const base = this.getBaseMass( key );
    if ( base === null )
      return null;
    return base * this.getMultiplier( key );
  }

  public static setMass( mass: number, key: string | null = this.active_key ): void
  {
    if ( key === null )
      return;

    const base = this.getBaseMass( key );
    if ( base === null || base <= 0 )
      return;

    const clamped = Math.min( this.HARD_MAX, Math.max( this.HARD_MIN, Number.isFinite( mass ) ? mass : base ) );
    this.multipliers.set( key, clamped / base );
  }

  public static isModified( key: string | null = this.active_key ): boolean
  {
    return this.getMultiplier( key ) !== 1;
  }

  public static modifiedCount(): number
  {
    let count = 0;
    for ( const value of this.multipliers.values() )
      if ( value !== 1 )
        count++;
    return count;
  }

  public static resetKey( key: string | null = this.active_key ): void
  {
    if ( key === null )
      return;
    this.multipliers.delete( key );
  }

  public static resetAll(): void
  {
    this.multipliers.clear();
  }
}
